import { useCallback, useContext, useSyncExternalStore } from 'react';

import { ContainerWidthContext } from 'utils/context/ContainerWidthContext';

const parseWidthQuery = (
  query: string,
): { type: 'max' | 'min'; px: number } | null => {
  const match = query.match(/\((max|min)-width:\s*(\d+)px\)/);
  if (!match) return null;
  return { type: match[1] as 'max' | 'min', px: Number(match[2]) };
};

interface QueryEntry {
  matches: boolean;
  subscribers: Set<() => void>;
}

// One MediaQueryList + one `change` listener per distinct query for the whole
// app. Previously every component instance registered its own window `resize`
// listener, so a chat that mounts thousands of message rows piled up tens of
// thousands of listeners and every add/remove became an O(n) scan.
const registry = new Map<string, QueryEntry>();

const getEntry = (query: string): QueryEntry => {
  const existing = registry.get(query);
  if (existing) return existing;

  const mediaQueryList = window.matchMedia(query);
  const entry: QueryEntry = {
    matches: mediaQueryList.matches,
    subscribers: new Set(),
  };
  mediaQueryList.addEventListener('change', () => {
    entry.matches = mediaQueryList.matches;
    entry.subscribers.forEach(notify => notify());
  });
  registry.set(query, entry);
  return entry;
};

const useMediaQuery = (query: string): boolean => {
  const containerWidth = useContext(ContainerWidthContext);

  const subscribe = useCallback(
    (notify: () => void) => {
      const entry = getEntry(query);
      entry.subscribers.add(notify);
      return () => {
        entry.subscribers.delete(notify);
      };
    },
    [query],
  );

  const getSnapshot = useCallback(() => getEntry(query).matches, [query]);

  const mediaMatches = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );

  const parsed = containerWidth != null ? parseWidthQuery(query) : null;
  if (containerWidth != null && parsed) {
    return parsed.type === 'max'
      ? containerWidth <= parsed.px
      : containerWidth >= parsed.px;
  }
  return mediaMatches;
};

export default useMediaQuery;
